import React, { createContext, useState } from 'react';
import axios from 'axios';
import config from '../config/config';

export const MedicaidContext = createContext();

export function MedicaidProvider(props) {
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // History state
  const [history, setHistory] = useState([]);
  const [historySnapshot, setHistorySnapshot] = useState(null);
  const [selectedHistoryDate, setSelectedHistoryDate] = useState(null);

  const getVariables = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${config.apiUrl}/api/medicaid/variables`);
      setVariables(response.data.variables || []);
    } catch (err) {
      console.error('Error fetching Medicaid variables:', err);
      setError(err.response?.data?.error || 'Failed to fetch variables');
    } finally {
      setLoading(false);
    }
  };

  const updateVariables = async (state, data, { skipHistory = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const url = `${config.apiUrl}/api/medicaid/variables/${state}${skipHistory ? '?skipHistory=true' : ''}`;
      const response = await axios.put(url, data);
      // Update local state with the new data
      setVariables(prev => {
        const existing = prev.findIndex(v => v.state === state);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = response.data;
          return updated;
        } else {
          return [...prev, response.data];
        }
      });
      // Refetch history after successful save
      await getHistory(state);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error updating Medicaid variables:', err);
      const errorMsg = err.response?.data?.error || 'Failed to update variables';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  const syncAllFromStaging = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${config.apiUrl}/api/medicaid/sync/import`);
      // Refresh variables after sync
      await getVariables();
      return { success: true };
    } catch (err) {
      console.error('Error syncing from staging:', err);
      const errorMsg = err.response?.data?.error || 'Failed to sync from staging';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Fetch history dates for a state
  const getHistory = async (state) => {
    if (!state) {
      setHistory([]);
      return;
    }
    try {
      const response = await axios.get(`${config.apiUrl}/api/medicaid/variables/${state}/history`);
      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Error fetching history:', err);
      setHistory([]);
    }
  };

  // Fetch a specific historical snapshot
  const getHistorySnapshot = async (state, date) => {
    if (!state || !date) {
      setHistorySnapshot(null);
      return null;
    }
    try {
      const response = await axios.get(`${config.apiUrl}/api/medicaid/variables/${state}/history/${date}`);
      setHistorySnapshot(response.data.snapshot);
      setSelectedHistoryDate(date);
      return response.data.snapshot;
    } catch (err) {
      console.error('Error fetching history snapshot:', err);
      setError(err.response?.data?.error || 'Failed to fetch historical version');
      return null;
    }
  };

  // Clear history selection (return to current data)
  const clearHistorySelection = () => {
    setSelectedHistoryDate(null);
    setHistorySnapshot(null);
  };

  return (
    <MedicaidContext.Provider
      value={{
        variables,
        loading,
        error,
        getVariables,
        updateVariables,
        syncAllFromStaging,
        setError,
        // History
        history,
        historySnapshot,
        selectedHistoryDate,
        getHistory,
        getHistorySnapshot,
        clearHistorySelection
      }}
    >
      {props.children}
    </MedicaidContext.Provider>
  );
}
